import { computed, unref } from 'vue';
import { usePlatformStore } from '@/stores/platform';
import { PLATFORM_CONNECTION } from '@/models/platform/platform-connection.enum';
import { META_CHANNELS, TIKTOK_CHANNELS } from '@/app/dashboards/reportConstants';

const AD_PLATFORM_CONFIG = [
  {
    name: 'Meta',
    platformConnection: PLATFORM_CONNECTION.FACEBOOK_ADS.value,
    channels: META_CHANNELS,
  },
  {
    name: 'TikTok',
    platformConnection: PLATFORM_CONNECTION.TIKTOK_ADS.value,
    channels: TIKTOK_CHANNELS,
  },
];

function getAdPlatformConfig(channel) {
  return AD_PLATFORM_CONFIG.find((config) => config.channels.includes(channel)) ?? null;
}

export function useAdAccountConnectionStatus({ brands } = {}) {
  const platformStore = usePlatformStore();

  function hasAdAccountForBrand(brandId, platform) {
    return platformStore.isPlatformConnected(platform, { brandId });
  }

  function hasAdAccountConnectedForPlatform(platform, brandsToCheck = null) {
    const brandsValue = unref(brandsToCheck) ?? unref(brands);
    if (!brandsValue || brandsValue.length === 0) return false;

    return brandsValue.some((brand) => {
      const brandId = brand?.id ?? brand;
      return hasAdAccountForBrand(brandId, platform);
    });
  }

  function hasAdAccountForChannel(channel, brandsToCheck = null) {
    const config = getAdPlatformConfig(channel);
    if (!config) return true;
    return hasAdAccountConnectedForPlatform(config.platformConnection, brandsToCheck);
  }

  const missingAdAccountChannels = computed(() =>
    AD_PLATFORM_CONFIG.filter(
      (config) => !hasAdAccountConnectedForPlatform(config.platformConnection),
    ).flatMap((config) => config.channels),
  );

  function getConnectionUrl(brandSlug) {
    if (!brandSlug) {
      return '/settings/social_platform';
    }
    return `/${brandSlug}/settings/social_platform`;
  }

  function getAdPlatformForChannel(channel) {
    return getAdPlatformConfig(channel)?.name ?? null;
  }

  function hasChannelForPlatform(platformName, channelsToCheck) {
    const config = AD_PLATFORM_CONFIG.find((c) => c.name === platformName);
    if (!config) return false;
    return channelsToCheck.some((ch) => config.channels.includes(ch));
  }

  function hasAdAccountConnectedForPlatformName(platformName) {
    const config = AD_PLATFORM_CONFIG.find((c) => c.name === platformName);
    if (!config) return false;
    return hasAdAccountConnectedForPlatform(config.platformConnection);
  }

  return {
    AD_PLATFORM_CONFIG,
    hasAdAccountForBrand,
    hasAdAccountForChannel,
    hasChannelForPlatform,
    hasAdAccountConnectedForPlatformName,
    missingAdAccountChannels,
    getConnectionUrl,
    getAdPlatformForChannel,
  };
}
